import { APP_VERSION, BACKEND_URL, IS_PRERENDERING } from '@/init/settings'
import { Sentry } from '@/services/sentry'

import { checkResponse, createStartEvent, persistStartedVariant } from './logic'
import { storage } from './storage'
import { ABVariantData, CheckResponseHint, Experiment } from './types'

function apiURL(path: string): string {
  let backendURL = BACKEND_URL

  if (
    backendURL.startsWith('http://localhost') &&
    window.location.hostname !== '127.0.0.1'
  ) {
    backendURL = backendURL.replace('localhost', window.location.hostname)
  }

  return `${backendURL}/api/${path}`
}

/**
 * Lightweight experiment start helper for public pages.
 *
 * Uses bare `fetch` instead of the shared axios request client, while reusing
 * the shared AB payload/storage logic.
 */
export async function startWithBareFetch(
  experiment: Experiment,
  variant: string | null = null,
): Promise<string | null> {
  try {
    if (IS_PRERENDERING) {
      return null
    }
    if (storage.hasExperiment(experiment)) {
      return storage.variant(experiment)
    }

    const clientId = storage.clientId()
    const data = await createStartEvent(experiment, variant, true)
    const response = await fetch(apiURL('abp/start'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Sf-Client': APP_VERSION,
      },
      body: JSON.stringify(data),
    })
    const responseData = await response.json().catch(() => null)

    if (response.status === 400 && responseData) {
      if (checkResponse(responseData, CheckResponseHint.abStartError)) {
        return storage.variant(experiment)
      }
    }

    if (!response.ok) {
      throw new Error(
        `Unexpected bare fetch start response: ${response.status}`,
      )
    }

    const variantData = (responseData as { data?: ABVariantData } | null)?.data
    if (!variantData) {
      throw new Error('Missing A/B variant data in bare fetch start response')
    }

    persistStartedVariant(experiment, variantData, clientId)

    return variantData.variant
  } catch (error: any) {
    Sentry.captureException(error)
    return null
  }
}
